import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiListPosts } from "../lib/api";
import SEO from "../components/SEO";
import { ChevronRight } from "lucide-react";

const formatDate = (iso) => {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
};

const BlogIndex = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiListPosts()
      .then(setPosts)
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <>
      <SEO
        title="Research Journal"
        description="Lab notes, research updates, and compliance commentary from Certified Wellness — for the research community."
        canonical="/blog"
      />
      <div className="max-w-[1100px] mx-auto px-4 lg:px-10 py-16">
        <nav className="text-[11px] tracking-[0.18em] uppercase text-gray-400 mb-5 flex items-center">
          <Link to="/" className="hover:text-[#0a0a0a]">Home</Link>
          <ChevronRight className="h-3 w-3 mx-1" />
          <span className="text-[#0a0a0a] font-semibold">Research Journal</span>
        </nav>

        <div className="flex items-center gap-3 mb-3">
          <div className="h-px w-8 bg-[#b91c1c]" />
          <span className="text-[10px] font-semibold tracking-[0.32em] uppercase text-[#b91c1c]">Research Journal</span>
        </div>
        <h1 className="font-display text-4xl md:text-5xl font-semibold text-[#0a0a0a] tracking-tight mb-3">
          Notes from the Lab.
        </h1>
        <p className="text-gray-500 max-w-2xl font-light">
          Lab notes, quality-control insights, and updates from our chemistry team — written for fellow researchers.
        </p>

        <div className="mt-12">
          {loading ? (
            <div className="text-sm text-gray-500" data-testid="blog-loading">Loading…</div>
          ) : posts.length === 0 ? (
            <div className="text-sm text-gray-500 border border-dashed border-gray-200 px-8 py-16 text-center" data-testid="blog-empty">
              No posts yet. Check back soon.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-12" data-testid="blog-list">
              {posts.map((p) => (
                <Link
                  key={p.id}
                  to={`/blog/${p.slug}`}
                  className="group block"
                  data-testid={`blog-card-${p.slug}`}
                >
                  {p.coverImage && (
                    <div className="aspect-[16/10] bg-white border border-gray-100 overflow-hidden mb-4">
                      <img src={p.coverImage} alt={p.title} className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500" />
                    </div>
                  )}
                  <div className="text-[10px] tracking-[0.25em] uppercase text-[#b91c1c] mb-2">
                    {formatDate(p.publishedAt || p.createdAt)}
                  </div>
                  <h2 className="font-display text-2xl font-semibold text-[#0a0a0a] leading-snug group-hover:text-[#b91c1c] transition-colors">
                    {p.title}
                  </h2>
                  {p.excerpt && <p className="mt-2 text-gray-600 font-light">{p.excerpt}</p>}
                  <div className="mt-3 text-[11px] tracking-[0.2em] uppercase text-gray-500 group-hover:text-[#0a0a0a] inline-flex items-center gap-1">
                    Read more <ChevronRight className="h-3 w-3" />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default BlogIndex;
